<template>
  <sakali-standard-layout v-if="league">
    <template v-slot:heading>
      <div class="row">
        <sakali-header :title="league.name" class="col-10 col-md-8">
          <template v-slot:super-title>
            <div class="d-inline-flex text-nowrap" v-on-clickaway="closeSeasonSelector">
              {{ league.challenge.name + '-' + league.season.name }}
              <sakali-season-selector v-model="selectedSeason"
                                      :challenge-id="league.challenge.id"
                                      :open="seasonSelectorOpen"
                                      class="ms-1 link"/>
            </div>
          </template>
        </sakali-header>
        <div class="col-2 col-md-4 d-flex justify-content-end small"
             v-on-clickaway="closeSelector">
          <sakali-team-and-coach-search-input
              class="mt-2"
              v-if="$mq !== 'mobile'"
              @input="navigateToLeagueFromTeam($event)"/>
          <fas
              type="button"
              icon="trophy"
              size="2x"
              class="mb-0 mt-3 ms-2 link"
              @click="leagueSelectorOpen = !leagueSelectorOpen"/>
          <sakali-league-selector
              class="position-absolute me-2 text-end"
              style="z-index: 99; right: 0;"
              v-model="selectedLeague"
              :open="leagueSelectorOpen"/>
        </div>
      </div>
    </template>
    <template v-slot:main v-if="roundInfo">
      <!-- Datum, Runde und Navigation -->
      <div class="row mt-2">
        <div class="col-9">
          {{ roundName }}
          <span> | {{ roundInfo.start | calendar }}</span>
        </div>
        <div class="col-3 text-nowrap text-end">
          <fas icon="chevron-left"
               size="lg"
               class="me-3 link"
               :class="{'text-secondary': roundInfo.numberInLeague <= 1}"
               @click="previousRound"></fas>
          <fas icon="chevron-right"
               class="link"
               size="lg"
               :class="{'text-secondary': roundInfo.numberInLeague >= league.roundCount}"
               @click="nextRound"></fas>
        </div>
      </div>
      <!-- Liste oder Vorschau anzeigen -->
      <div class="mt-1" v-if="$mq === 'mobile' && !showList && selectedMatch && matchesInFuture">
        <sakali-match-preview
            v-if="roundInfo.matches"
            :match="selectedMatch"
            @toggle-view="showList = !showList">
        </sakali-match-preview>
      </div>
      <div v-else>
        <sakali-match-list
            v-if="roundInfo.matches"
            :matches="roundInfo.matches"
            :selected-match="selectedMatch"
            :ko="roundInfo.ko"
            @toggle-view="showList = !showList"
            @select="selectedMatch = $event">
        </sakali-match-list>
      </div>
      <div class="mt-1" v-if="roundInfo && !roundInfo.ko">
        <sakali-table :roundInfo="roundInfo"
                      :league="league"/>
      </div>
    </template>
    <template v-slot:right1>
      <portlet v-if="!running && !finished && selectedMatch">
        <template v-slot:heading>Vorschau</template>
        <template v-slot:content>
          <sakali-match-preview :match="selectedMatch"/>
        </template>
      </portlet>
      <portlet v-if="selectedMatch && (running || finished)">
        <template v-slot:heading>
          <span class="live" v-if="running">Live </span>Spielbericht
        </template>
        <template v-slot:content>
          <sakali-match-report :match-id="selectedMatch.id"/>
        </template>
      </portlet>
    </template>
    <template v-slot:right2 v-if="roundInfo && !roundInfo.ko">
      <portlet v-if="finished && roundInfo">
        <template v-slot:heading>
          Mannschaft des Tages
        </template>
        <template v-slot:content>
          <sakali-team-of-the-day :round-id="roundInfo.id"/>
        </template>
      </portlet>
    </template>
  </sakali-standard-layout>
</template>

<script>
import {mixin as clickaway} from 'vue-clickaway';
import {mapActions, mapGetters} from 'vuex';
import sakaliTable from '../components/league/LeagueRanking';
import sakaliMatchPreview from '../components/league/MatchPreview';
import sakaliMatchList from '../components/league/MatchList';
import sakaliLeagueSelector from "../components/league/ChallengeAndLeagueSelector";
import sakaliSeasonSelector from "@/components/league/SeasonSelector";
import sakaliStandardLayout from "@/layouts/StandardLayout"
import sakaliTeamOfTheDay from "@/components/league/TeamOfTheDay";
import sakaliMatchReport from "@/components/league/MatchReport"
import sakaliTeamAndCoachSearchInput from "@/components/TeamAndCoachSearchInput"

import Portlet from "@/layouts/Portlet";

export default {
  props: {
    leagueId: String,
    roundNumber: String
  },
  data() {
    return {
      league: null,
      selectedLeague: null,
      roundInfo: null,
      showList: false,
      leagueSelectorOpen: false,
      selectedSeason: null,
      seasonSelectorOpen: false,
      selectedMatch: null
    }
  },
  computed: {
    ...mapGetters(['configuration', 'userId']),
    running() {
      return this.roundInfo && this.roundInfo.running
    },
    finished() {
      return this.roundInfo && this.roundInfo.finished
    },
    started() {
      return this.running || this.finished
    },
    matchesInFuture() {
      return this.roundInfo && new Date(this.roundInfo.start) > new Date().getTime()
    },
    roundName() {
      if (this.league.challenge.koChallenge) {
        let koRoundNameCount = this.configuration.koRoundNames.length
        let backwardRoundNumber = this.league.roundCount - this.roundInfo.numberInLeague
        if (backwardRoundNumber < koRoundNameCount)
          return this.configuration.koRoundNames[backwardRoundNumber]
        else
          return this.roundInfo.numberInLeague + '. Hauptrunde'
      } else
        return this.roundInfo.numberInLeague + '. Spieltag'
    }
  },
  methods: {
    ...mapActions(['handleErrorAction']),
    updateView() {
      if (!this.leagueId) {
        this.selectedSeason = null
        this.$api.get('/leagues/focus').then(response => {
          this.$router.push({
            name: 'round',
            params: {
              leagueId: String(response.data.id),
              roundNumber: String(response.data.currentRoundNumber)
            }
          })
        }).catch(error => this.handleErrorAction(error))
      } else {
        this.$api.get(`/leagues/${this.leagueId}`).then(response => {
          this.league = response.data
          this.loadRound()
        }).catch(error => this.handleErrorAction(error))
      }
    },
    closeSelector() {
      this.leagueSelectorOpen = false
    },
    closeSeasonSelector() {
      this.seasonSelectorOpen = false
    },
    nextRound() {
      if (this.roundNumber < this.league.roundCount) {
        this.$router.push({
          name: 'round',
          params: {
            leagueId: String(this.leagueId),
            roundNumber: String(this.roundInfo.numberInLeague + 1)
          }
        })
      }
    },
    previousRound() {
      if (this.roundNumber > 1) {
        this.$router.push({
          name: 'round',
          params: {
            leagueId: String(this.league.id),
            roundNumber: String(this.roundInfo.numberInLeague - 1)
          }
        })
      }
    },
    loadRound() {
      this.selectedMatch = null
      this.$api.get('/rounds', {
        params: {
          leagueId: Number(this.leagueId),
          number: Number(this.roundNumber)
        }
      }).then(response => {
        this.roundInfo = response.data;
        for (let m of this.roundInfo.matches) {
          if (m.team1.coach.id === this.userId || m.team2.coach.id === this.userId) {
            this.selectedMatch = m
            break
          }
        }
        if (!this.selectedMatch)
          this.selectedMatch = this.roundInfo.matches[0]
      }).catch(error => {
        this.handleErrorAction(error)
      })
    },
    navigateToLeagueFromTeam(team) {
      this.$api.get('/leagues', {
        params: {
          team: team.id
        }
      }).then(response => this.selectedLeague = response.data)
          .catch(error => this.handleError(error))
    }
  },
  watch: {
    $route() {
      if (this.$route.params.leagueId)
        this.updateView()
    },
    selectedLeague() {
      this.leagueSelectorOpen = false
      let roundNumber = this.selectedLeague.challenge.id === this.league.challenge.id
          ? Number(this.roundNumber) : this.selectedLeague.currentRoundNumber
      if (this.selectedLeague.id !== Number(this.leagueId)
          || roundNumber !== Number(this.roundNumber)) {
        this.$router.push({
          name: 'round',
          params: {
            leagueId: String(this.selectedLeague.id),
            roundNumber: String(roundNumber)
          }
        })
      }
    },
    selectedSeason() {
      if (this.selectedSeason) {
        this.$api.get('/leagues', {
          params: {
            'season': this.selectedSeason.id,
            'level': this.league.level,
            'index': this.league.indexInLevel
          }
        }).then(response => {
          this.selectedSeason = null
          this.$router.push({
            name: 'round',
            params: {
              leagueId: String(response.data.id),
              roundNumber: String(this.roundNumber)
            }
          })
        }).catch(error => this.handleErrorAction(error))
      }
    },
    running() {
      if (!this.showList)
        this.showList = true;
    }
  },
  mounted() {
    if (!this.leagueId) {
      const vm = this
      this.$api.get('/leagues/focus').then(response => {
        vm.$router.replace({
          name: 'round',
          params: {
            leagueId: String(response.data.id),
            roundNumber: String(response.data.currentRoundNumber)
          }
        })
      }).catch(error => this.handleErrorAction(error))
    } else {
      this.updateView()
    }
  },
  components: {
    Portlet,
    sakaliTeamOfTheDay,
    sakaliSeasonSelector,
    sakaliTable,
    sakaliMatchPreview,
    sakaliMatchList,
    sakaliLeagueSelector,
    sakaliStandardLayout,
    sakaliMatchReport,
    sakaliTeamAndCoachSearchInput
  },
  mixins: [clickaway]
}
</script>
